import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import InstructorSidebar from '../common/instructorSidebar'
import {
  getCourseAnalytics,
  getCourseDetail,
  CourseAnalyticsData,
  dowloadCBTResult
} from '../../../api/courses'

import { json2csv } from 'json-2-csv';
import dayjs from 'dayjs'

// Custom formatter for API revenue data (handles format like "513,200.00")
const formatRevenueFromAPI = (value: string | number): string => {
  if (!value) return 'Rp0'

  // Convert to string and remove any existing commas
  const cleanValue = value.toString().replace(/,/g, '')

  // Parse as float to handle decimal values
  const numValue = parseFloat(cleanValue)

  if (isNaN(numValue)) return 'Rp0'

  // Format with Indonesian locale (dots as thousand separators)
  return `Rp${numValue.toLocaleString('id-ID', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`
}

const CourseAnalytics = () => {
  const { id } = useParams<{ id: string }>()
  const [chartType, setChartType] = useState<'monthly' | 'yearly'>('monthly')
  const [dataType, setDataType] = useState<'transaction' | 'revenue'>(
    'transaction',
  )

  // Fetch course details
  const { data: courseData } = useQuery({
    queryKey: ['course-detail', id],
    queryFn: () => getCourseDetail(id!, true),
    enabled: !!id,
  })

  // Fetch analytics data
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['course-analytics', id],
    queryFn: () => getCourseAnalytics(id!),
    enabled: !!id,
  })

  const analytics: CourseAnalyticsData | undefined = analyticsData?.data

  // Prepare chart data
  const getChartData = () => {
    if (!analytics) return { categories: [], data: [] }

    const chartData = analytics.chart[dataType][chartType]

    if (chartType === 'monthly') {
      const monthlyData = chartData as any
      return {
        categories: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
        data: [
          monthlyData.week_1,
          monthlyData.week_2,
          monthlyData.week_3,
          monthlyData.week_4,
        ].map((val) =>
          dataType === 'revenue' ? parseFloat(val as string) : (val as number),
        ),
      }
    } else {
      const yearlyData = chartData as any
      return {
        categories: [
          'Jan',
          'Feb',
          'Mar',
          'Apr',
          'May',
          'Jun',
          'Jul',
          'Aug',
          'Sep',
          'Oct',
          'Nov',
          'Dec',
        ],
        data: [
          yearlyData.january,
          yearlyData.february,
          yearlyData.march,
          yearlyData.april,
          yearlyData.may,
          yearlyData.june,
          yearlyData.july,
          yearlyData.august,
          yearlyData.september,
          yearlyData.october,
          yearlyData.november,
          yearlyData.december,
        ].map((val) =>
          dataType === 'revenue' ? parseFloat(val as string) : (val as number),
        ),
      }
    }
  }

  const chartData = getChartData()

  const chartOptions: ApexOptions = {
    chart: {
      type: 'bar',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
        borderRadius: 4,
      },
    },
    dataLabels: {
      enabled: false,
    },
    stroke: {
      show: true,
      width: 2,
      colors: ['transparent'],
    },
    xaxis: {
      categories: chartData.categories,
    },
    yaxis: {
      title: {
        text: dataType === 'revenue' ? 'Revenue (IDR)' : 'Transactions',
      },
      labels: {
        formatter: (val) => {
          if (dataType === 'revenue') {
            return formatRevenueFromAPI(val.toString())
          }
          return val.toString()
        },
      },
    },
    fill: {
      opacity: 1,
    },
    tooltip: {
      y: {
        formatter: (val) => {
          if (dataType === 'revenue') {
            return formatRevenueFromAPI(val.toString())
          }
          return val + ' transactions'
        },
      },
    },
    colors: ['#3b82f6'],
  }

  const chartSeries = [
    {
      name: dataType === 'revenue' ? 'Revenue' : 'Transactions',
      data: chartData.data,
    },
  ]

  const handleDownloadCBT = async (courseId: string) => {
    try {
      // 1️⃣ Fetch the analytics data from API
      const res = await dowloadCBTResult(courseId);

      // 2️⃣ Extract analytics array (handle flexible API response)
      const analyticsData = res?.course?.analytics ?? [];

      if (!Array.isArray(analyticsData) || analyticsData.length === 0) {
        alert('Tidak ada data CBT untuk course ini.');
        return;
      }

      // 3️⃣ Map to CSV-friendly format
      const csvData = analyticsData.map((a: any) => {
        const start = a.attempt_start_at ? dayjs(a.attempt_start_at) : null;
        const end = a.attempt_end_at ? dayjs(a.attempt_end_at) : null;

        // Format: DD-MM-YYYY HH:mm
        const formattedStart = start ? start.format('DD-MM-YYYY HH:mm') : '-';
        const formattedEnd = end ? end.format('DD-MM-YYYY HH:mm') : '-';

        // Calculate duration (in minutes)
        const duration =
          start && end ? end.diff(start, 'minute') + ' menit' : '-';

        return {
          'Nama Peserta': a.user_name || '-',
          'Email': a.email || '-',
          'Nomor HP': a.phone_number || '-',
          'Waktu Mulai': formattedStart,
          'Waktu Selesai': formattedEnd,
          'Durasi (Menit)': duration,
          'Jumlah Benar': a.jumlah_benar ?? 0,
          'Jumlah Salah': a.jumlah_salah ?? 0,
          'Score (%)': a.score ?? 0,
        };
      });

      // 4️⃣ Convert JSON → CSV
      const csv = await json2csv(csvData);

      // 5️⃣ Trigger CSV download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `cbt-result-${res.course.title.replace(/\s+/g, '_')}-${new Date().toLocaleDateString('id-ID')}.csv`;
      document.body.appendChild(link);
      link.click();

      // cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('✅ CBT Result CSV generated successfully');
    } catch (error) {
      console.error('❌ Error generating CBT CSV:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="content">
        <div className="container">
          <div className="row">
            <InstructorSidebar />
            <div className="col-lg-9">
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: '400px' }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container">
        <div className="row">
          <InstructorSidebar />

          <div className="col-lg-9">
            <div className="page-title d-flex align-items-center justify-content-between mb-4">
              <div>
                <h5 className="fw-bold">CBT Analytics</h5>
                <p className="text-muted mb-0">{courseData?.title}</p>
              </div>
              <div className="d-flex align-items-center justify-content-end">
                <button
                  className="btn btn-info me-3"
                  onClick={() => handleDownloadCBT(courseData.id.toString())}
                >
                  <i className="isax isax-arrow-down me-2" />
                  Download Hasil CBT
                </button>

                <button
                  className="btn btn-outline-secondary"
                  onClick={() => window.history.back()}
                >
                  <i className="isax isax-arrow-left me-2" />
                  Back
                </button>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="row mb-4">
              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="mb-3">
                      <i className="isax isax-receipt-2-1 fs-1 text-primary" />
                    </div>
                    <h4 className="fw-bold text-primary mb-1">
                      {analytics?.transaction_count || '0'}
                    </h4>
                    <p className="text-muted mb-0">Total Transactions</p>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="mb-3">
                      <i className="isax isax-money-4 fs-1 text-success" />
                    </div>
                    <h4 className="fw-bold text-success mb-1">
                      {formatRevenueFromAPI(analytics?.revenue || '0')}
                    </h4>
                    <p className="text-muted mb-0">Total Revenue</p>
                  </div>
                </div>
              </div>

              <div className="col-md-4">
                <div className="card border-0 shadow-sm">
                  <div className="card-body text-center">
                    <div className="mb-3">
                      <i className="isax isax-award fs-1 text-warning" />
                    </div>
                    <h4 className="fw-bold text-warning mb-1">
                      {analytics?.certificate_count || 0}
                    </h4>
                    <p className="text-muted mb-0">Certificates Issued</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Chart Section */}
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-transparent border-0 pb-0">
                <div className="d-flex justify-content-between align-items-center">
                  <h6 className="fw-bold mb-0">
                    {dataType === 'revenue' ? 'Revenue' : 'Transaction'}{' '}
                    Analytics
                  </h6>

                  <div className="d-flex gap-2">
                    {/* Data Type Toggle */}
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${dataType === 'transaction'
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                          }`}
                        onClick={() => setDataType('transaction')}
                      >
                        Transactions
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${dataType === 'revenue'
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                          }`}
                        onClick={() => setDataType('revenue')}
                      >
                        Revenue
                      </button>
                    </div>

                    {/* Chart Type Toggle */}
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm ${chartType === 'monthly'
                          ? 'btn-secondary'
                          : 'btn-outline-secondary'
                          }`}
                        onClick={() => setChartType('monthly')}
                      >
                        Monthly
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm ${chartType === 'yearly'
                          ? 'btn-secondary'
                          : 'btn-outline-secondary'
                          }`}
                        onClick={() => setChartType('yearly')}
                      >
                        Yearly
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card-body">
                <Chart
                  options={chartOptions}
                  series={chartSeries}
                  type="bar"
                  height={350}
                />
              </div>
            </div>

            {/* Data Summary Table */}
            <div className="card border-0 shadow-sm mt-4">
              <div className="card-header bg-transparent border-0">
                <h6 className="fw-bold mb-0">Data Summary</h6>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6">
                    <h6 className="fw-medium mb-3">
                      {chartType === 'monthly' ? 'Weekly' : 'Monthly'}{' '}
                      {dataType === 'revenue' ? 'Revenue' : 'Transactions'}
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th>
                              {dataType === 'revenue' ? 'Revenue' : 'Count'}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chartData.categories.map((category, index) => (
                            <tr key={category}>
                              <td>{category}</td>
                              <td>
                                {dataType === 'revenue'
                                  ? formatRevenueFromAPI(
                                    chartData.data[index].toString(),
                                  )
                                  : chartData.data[index]}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <h6 className="fw-medium mb-3">Quick Stats</h6>
                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Average per period:</span>
                        <span className="fw-medium">
                          {dataType === 'revenue'
                            ? formatRevenueFromAPI(
                              (
                                chartData.data.reduce((a, b) => a + b, 0) /
                                chartData.data.length
                              ).toString(),
                            )
                            : Math.round(
                              chartData.data.reduce((a, b) => a + b, 0) /
                              chartData.data.length,
                            )}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Highest period:</span>
                        <span className="fw-medium">
                          {
                            chartData.categories[
                            chartData.data.indexOf(
                              Math.max(...chartData.data),
                            )
                            ]
                          }
                        </span>
                      </div>
                      <div className="d-flex justify-content-between">
                        <span className="text-muted">Peak value:</span>
                        <span className="fw-medium">
                          {dataType === 'revenue'
                            ? formatRevenueFromAPI(
                              Math.max(...chartData.data).toString(),
                            )
                            : Math.max(...chartData.data)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CourseAnalytics
